/**
 * Module dependencies.
 */

import { apiEndpoints } from 'app/core/config/api-endpoints';
import { appRoutes } from 'app/routes';
import { identity, pickBy } from 'lodash';
import { CrudListType, CrudTemplate, ListColumns } from '../../../organisms/crud/form-types';
import { ListFilters } from 'app/components/organisms/crud/form-filters/form-filters-type';
import { TFunction } from 'react-i18next';
import { useCrudRequest } from 'app/hooks/app-requests/crud/use-crud-request';
import { DateTime } from 'app/components/atoms/date-time/date-time';
import React from 'react';

/**
 * List labels.
 */

const listColumns = (translate: TFunction): ListColumns<any> => [
  {
    title: translate('common.table.columns.index'),
    dataIndex: 'index',
    key: 'index',
    render: item => item.index
  },
  {
    title: translate('common.table.columns.medicationName'),
    dataIndex: 'medicationName',
    size: '1fr',
    key: 'medicationName',
    render: item => JSON.parse(item.medicationName).pt
  },
  {
    title: translate('common.table.columns.scheduleAlarmId'),
    dataIndex: 'scheduleAlarmId',
    key: 'scheduleAlarmId',
    render: item => item.scheduleAlarmId
  },
  {
    title: translate('common.table.columns.fireDate'),
    dataIndex: 'fireDate',
    key: 'fireDate',
    size: '300px',
    render: item => <DateTime date={item.fireDate} />
  },
  {
    title: translate('common.table.columns.createdAt'),
    dataIndex: 'createdAt',
    key: 'createdAt',
    size: '200px',
    render: item => <DateTime date={item.createdAt} />
  },
  {
    title: translate('common.table.columns.updatedAt'),
    dataIndex: 'updatedAt',
    key: 'updatedAt',
    size: '200px',
    render: item => <DateTime date={item.updatedAt} />
  }
];

/**
 * List filters.
 */

const listFilters = (translate: TFunction): ListFilters => [
  {
    type: 'inputSearch',
    inline: true,
    name: 'search',
    label: translate('common.labels.search')
  },
  {
    type: 'selectSearch',
    name: 'device',
    inline: true,
    style: {
      minWidth: 230
    },
    label: translate('common.labels.device'),
    showSearch: true,
    allowClear: true,
    options: {
      hook: useCrudRequest as any,
      hookProps: (values: any, params: any) => {
        return [
          {
            key: ['devicesByUserId'],
            options: {
              interpolations: params,
              params: { userId: params.userId }
            },
            endpoint: apiEndpoints.devices
          }
        ];
      },
      normalize: (items: any) => [
        {
          label: translate('common.labels.empty'),
          value: null
        },
        ...(items?.map((item: any) => ({
          value: item.id,
          label: `${item.operatingSystem}: ${item.deviceName}  - ${item.appVersion}`
        })) ?? [])
      ]
    }
  }
];

/**
 * List.
 */

function list(translate: TFunction): CrudListType<any> {
  return {
    columns: listColumns(translate),
    filters: listFilters(translate),
    route: appRoutes.dashboard.users.detail.definedAlarms,
    enableExport: false,
    canEdit: false,
    canRemove: false,
    resolveParams: (params: any, item?: any) => {
      return {
        userId: params.userId,
        settingId: params.settingId ?? item?.id
      };
    },
    key: ['userDefinedAlarms'],
    normalizeFilters: (data: any) => {
      const { ...filters } = data.filters;
      const result: any = {
        filters: pickBy(filters, identity),
        order: []
      };

      return result;
    },
    normalizeData: (result: any) => {
      return result;
    },
    pagination: {
      pageSize: 1000,
      pageSizeOptions: [10, 25, 50, 100, 500, 1000]
    },
    endpoint: apiEndpoints.userDefinedAlarms,
    endpointById: apiEndpoints.userDefinedAlarmsById
  };
}

/**
 * Config.
 */

export function createUserDefinedAlarmsTemplate(translate: TFunction): CrudTemplate<any> {
  return {
    list: list(translate)
  };
}
